import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Box } from "@mui/material";
import { useAuth } from "../../components/contexts/AuthContext";
import { getData } from "../../components/hooks/getData";
import apiClient from "../../api-client";
import {
  NotificationPanel,
  NOTIFICATION_STATUS_FILTERS,
} from "./components/NotificationPanel";

export function TicketFreelancerNotifications() {
  const { user: currentUser, interceptorReady } = useAuth();
  const { setPageContext, dashboardRefreshKey } = useOutletContext();
  const navigate = useNavigate();
  const [selectedNotificationFilter, setSelectedNotificationFilter] =
    useState("unread");
  const [notificationPage, setNotificationPage] = useState(0);
  const [notificationPageSize, setNotificationPageSize] = useState(25);
  const [notifications, setNotifications] = useState([]);
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false);
  const [clearingThreadId, setClearingThreadId] = useState(null);

  useEffect(() => {
    setPageContext({
      title: "Notificaciones",
      subtitle: "Actividad y cambios de tus tickets asignados",
      showTitle: true,
    });
  }, [setPageContext]);

  const endpoint = useMemo(() => {
    if (!interceptorReady || !currentUser) return null;

    const params = new URLSearchParams();
    const filterConfig = NOTIFICATION_STATUS_FILTERS[selectedNotificationFilter];

    if (filterConfig?.isRead !== undefined) {
      params.set("is_read", filterConfig.isRead ? "true" : "false");
    }

    params.set("page", String(notificationPage + 1));
    params.set("page_size", String(notificationPageSize));

    return `dashboard/notifications/?${params.toString()}`;
  }, [
    interceptorReady,
    currentUser,
    selectedNotificationFilter,
    notificationPage,
    notificationPageSize,
  ]);

  const {
    data: notificationsResponse,
    error: notificationsError,
    isLoading: notificationsIsLoading,
    isInitialLoading: notificationsIsInitialLoading,
  } = getData(endpoint, Boolean(endpoint), [
    currentUser?.id,
    dashboardRefreshKey,
    selectedNotificationFilter,
    notificationPage,
    notificationPageSize,
  ]);

  useEffect(() => {
    if (!notificationsResponse) {
      setNotifications([]);
      return;
    }

    const nextNotifications = Array.isArray(notificationsResponse)
      ? notificationsResponse
      : notificationsResponse.results || [];

    setNotifications(nextNotifications);
  }, [notificationsResponse]);

  useEffect(() => {
    setNotificationPage(0);
  }, [selectedNotificationFilter, dashboardRefreshKey]);

  const unreadCount =
    selectedNotificationFilter === "unread"
      ? notificationsResponse?.count ?? notifications.length
      : 0;

  const totalCount = Array.isArray(notificationsResponse)
    ? notificationsResponse.length
    : notificationsResponse?.count ?? 0;

  const hasNotifications = notifications.length > 0;
  const isLoading =
    !notificationsResponse &&
    (notificationsIsLoading || notificationsIsInitialLoading);

  const getNotificationRoute = (notification) => {
    if (!notification) return null;

    if (
      notification.ticket_id &&
      (
        notification.thread_type === "ticket" ||
        notification.type === "ticket_assigned" ||
        notification.type === "ticket_status_changed" ||
        notification.type === "ticket_created"
      )
    ) {
      return `/tickets/${notification.ticket_id}/?t=${Date.now()}`;
    }

    return null;
  };

  const handleNotificationClick = async (notification) => {
    const route = getNotificationRoute(notification);
    if (!route) return;

    // Mark opened (lightweight signal)
    try {
      await apiClient.post("dashboard/notifications/mark-opened/", {
        ids: [notification.id],
      });
    } catch {
      // no-op
    }

    // clear-thread para unread_in_thread (igual que el resto de roles)
    if (notification.thread_id && notification.type === "unread_in_thread") {
      setClearingThreadId(notification.thread_id);
      setNotifications((prev) =>
        prev.filter(
          (n) =>
            !(n.thread_id === notification.thread_id && n.type === "unread_in_thread")
        )
      );
      try {
        await apiClient.post("dashboard/notifications/clear-thread/", {
          thread_id: notification.thread_id,
        });
      } catch {
        // no-op
      } finally {
        setClearingThreadId(null);
      }
    } else if (!notification.is_read) {
      // mark-read para el resto de notificaciones no leídas
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notification.id ? { ...n, is_read: true } : n
        )
      );
      try {
        await apiClient.post("dashboard/notifications/mark-read/", {
          ids: [notification.id],
        });
      } catch {
        // no-op
      }
    }

    navigate(route);
  };

  const handleMarkRead = async (notificationId) => {
    await apiClient.post("dashboard/notifications/mark-read/", {
      ids: [notificationId],
    });
    setNotifications((prev) => prev.filter((item) => item.id !== notificationId));
  };

  const handleMarkAllRead = async () => {
    setIsMarkingAllRead(true);
    try {
      await apiClient.post("dashboard/notifications/mark-all-read/");
      setNotifications([]);
    } finally {
      setIsMarkingAllRead(false);
    }
  };

  return (
    <Box sx={{ maxWidth: "1100px" }}>
      <NotificationPanel
        notifications={notifications}
        isLoading={isLoading}
        error={notificationsError}
        unreadCount={unreadCount}
        isUnreadCountLoading={isLoading}
        onMarkAllRead={handleMarkAllRead}
        onMarkRead={handleMarkRead}
        onNotificationClick={handleNotificationClick}
        isMarkingAllRead={isMarkingAllRead}
        clearingThreadId={clearingThreadId}
        currentUser={currentUser}
        clientNameById={{}}
        getNotificationSummary={(notification) =>
          notification?.summary || "Nueva actividad registrada."
        }
        getNotificationRoute={getNotificationRoute}
        selectedNotificationFilter={selectedNotificationFilter}
        hasNotifications={hasNotifications}
        showFilters
        onFilterChange={setSelectedNotificationFilter}
        showPagination
        page={notificationPage}
        rowsPerPage={notificationPageSize}
        totalCount={totalCount}
        onPageChange={(_, page) => setNotificationPage(page)}
        onRowsPerPageChange={(event) => {
          setNotificationPageSize(parseInt(event.target.value, 10));
          setNotificationPage(0);
        }}
        sx={{
          maxHeight: "calc(100vh - 230px)",
        }}
      />
    </Box>
  );
}

export default TicketFreelancerNotifications;
